/**
 * Component: Slick Slider
 *
 * Works like data-slick but with custom features (eg. removeClasses option)
 *
 * - Data Attr:   `data-c-slick`
 * - Styles:      n/a
 *
 * @example <div data-c-slick='{ "slidesToShow": 2 }'> ... </div>
 *
 * @link https://github.com/kenwheeler/slick
 *
 * @module components/CSlick
 */


// Dependances
// ============================================================================

import { $ } from '../common/utility/u-jquery-$';
import { IComponentSetup } from './shared/s-component-setup';
import { UArray } from '../common/utility/u-array';


// Internal Variables
// ============================================================================

/**
 * Default options, overwritten with JSON on the data attribute
 *
 * All available Slick options can also be used
 *
 * This jDoc only covers the custom options bespoke to this component
 *
 * @link https://github.com/kenwheeler/slick/#settings
 * @link https://github.com/kenwheeler/slick#responsive-option-example
 *
 * @var {Object}
 *
 * @property {bool}     [debugComponent=false]    True to enable extensive logging. Errors and warnings are always logged regardless
 * @property {String[]} [removeClasses=[]]        Custom - Remove the specified classes form the container and its children, just before the slider inits. Lets you use classes to preserve layout prior to the Slick init
 * @property {bool}     [setMinHeights=false]     Custom: Vertical - If true, a min-height is applied to all slides, using the tallest slide's height
 * @property {bool}     [verticalHeightFix=false] Custom: Vertical - If true, applies fixes for vertical slides
 * @property {bool}     [slideOnHover=false]      Custom: Sets the slider to slide when the user hovers the slick arrows.
 */
const defaultOptions = {
	debugComponent: false,

	// Common
	arrows:          false,
	autoplay:        false,
	autoplaySpeed:   3000,
	dots:            false,
	infinite:        true,
	slidesToScroll:  2,
	slidesToShow:    2,
	speed:           300,

	// Responsive (main BPs: 640, 768, 1024, 1280, 1366)
	responsive: [
		// Example:
		// { "breakpoint":  768, "settings": { "arrows": false, "dots": true, "slidesToShow": 1, "slidesToScroll": 1 } }
	],

	// Grid
	rows:            1, // Grid mode: number of rows
	slidesPerRow:    1, // Grid mode: number of slides per row

	// Vertical
	vertical:        false, // Vertical: Enable vertical slides  (note: Vertical sliders may need to use the "setMinHeights)
	verticalSwiping: false, // Vertical: Enable vertical swiping (note: Vertical sliders may need to use the "setMinHeights)

	// Misc
	initialSlide:    0,    // Slide to start on
	swipeToSlide:    true, // If true, dragging to change slides ignores the "slidesToScroll" option, and moves by the number of slides you dragged instead
	touchThreshold:  20,   // Touch threshold. Higher number means less dragging is needed to advance slides


	// Custom
	// ------------------------------------------------------------

	// Removes the specified classes from the container and its children, just
	// before the slider inits. Lets you use classes to preserve layout prior
	// to the Slick init
	removeClasses: [],

	// Vertical Slider: Fix Slick's poor support for vertical slides
	setMinHeights: false,     // Custom: Vertical - If true, a min-height is applied to all slides, using the tallest slide's height
	verticalHeightFix: false, // Custom: Vertical - If true, applies fixes for vertical slides

	slideOnHover: false, // Custom: If true, allows the slider to be scrolled by hovering the arrows.
}


// Init
// ============================================================================

/**
 * Core component init
 *
 * @return  {void}
 */
function _componentInit()
{
	const setupOpts = {
		name:        'slick',
		dataAttr:    'data-c-slick',
		defaultOpts: defaultOptions,
		Class:       SlickComponent,
		debugSetup:  false,
	};

	IComponentSetup.setupComponent( setupOpts );
}


// Single Component Class
// ============================================================================

class SlickComponent
{
	/**
	 * Slick component constructor
	 *
	 * @param   {jQuery}  $container  jQuery container element
	 * @param   {object}  options     Options object
	 *
	 * @return  {void}
	 */
	constructor( $container, options, instanceIndex )
	{
		// Always available to all components
		this.$container    = $container;
		this.options       = options;
		this.instanceIndex = instanceIndex;

		// Custom to slick
		// nothing yet...
	}

	/**
	 * Main init
	 *
	 * Run automatically via setupComponent
	 */
	_classInit()
	{
		this.setupItem();
	}

	/**
	 * Set up a single item
	 */
	setupItem()
	{
		const { $container, options } = this;

		this.setupSlickEventTriggers();
		this.setupInternalEvents();
		this.removeClasses();
		this.customButtonsOverrides();

		// Init slick
		$container.slick( options );

		// Apply post-init fixes
		this.applyVerticalHeightFix();
		this.slideOnHover();
	}

	/**
	 * Apply custom replacements for the prev/next button HTML. Using this
	 * means the markup doesn't get bloated by adding button HTML to the data
	 * attributes
	 */
	customButtonsOverrides()
	{
		// V3 global slick buttons, set via: v3/functions/v3-f-javascript.php
		if ( this.options.prevArrow === 'v3SlickGlobals:prevArrow' )
		{
			this.options.prevArrow = JSON.parse( window.v3SlickGlobals['prevArrow'] );
		}

		if ( this.options.nextArrow === 'v3SlickGlobals:nextArrow' )
		{
			this.options.nextArrow = JSON.parse( window.v3SlickGlobals['nextArrow'] );
		}
	}

	/**
	 * Remove classes from slides (or any other element inside $container)
	 *
	 * @return  {void}
	 */
	removeClasses()
	{
		const { $container, options } = this;

		if ( UArray.safeArrayCount( options.removeClasses ) )
		{
			options.removeClasses.forEach( removeCls =>
			{
				if ( removeCls.length )
				{
					// Remove class from container
					if ( $container.hasClass( removeCls ) )
					{
						$container.removeClass( removeCls );
					}

					const $target = $container.find( '.' + removeCls );

					// Remove class from child
					if ( $target.length )
					{
						$target.removeClass( removeCls );

						if ( options.debugComponent )
						{
							console.info( `[c-slick] Removed the class "${removeCls}"'`, { $target, removeCls } );
						}
					}
					else
					{
						if ( options.debugComponent )
						{
							console.info( `[c-slick] Tried to remove the class "${removeCls}", but not matching element was found` );
						}
					}
				}
			});
		}
	}


	// Events
	// --------------------------------------------------------------------

	/**
	 * Setup event listeners/triggers for all available Slick events.
	 *
	 * Slick triggers its own events, but you have to set up event listeners
	 * for them BEFORE Slick inits. This func sets them up for you.
	 *
	 * See the Slick docs for more info on each event (search for "Events").
	 *
	 * Note that this component's events use the same prefix as other
	 * components. The main event name is still the same (eg, for
	 * "afterChange"), listen for the event "c-slick:afterChange").
	 *
	 * However, unlike Slick's events, these events also provide the class
	 * instance as the first returned param
	 *
	 * @link https://kenwheeler.github.io/slick
	 *
	 * @param   {jQuery}  $container  jQuery slider container element
	 * @param   {object}  options     Slider options object
	 *
	 * @return  {void}
	 */
	setupSlickEventTriggers()
	{
		const { $container } = this;

		// Fires after slide change
		$container.on( 'afterChange', ( event, slick, currentSlide, nextSlide ) => {
			$container.trigger( 'c-slick:afterChange', this, $container, event, slick, currentSlide, nextSlide  );
		});

		// Fires before slide change
		$container.on( 'beforeChange', ( event, slick, currentSlide, nextSlide ) => {
			$container.trigger( 'c-slick:beforeChange', this, $container, event, slick, currentSlide, nextSlide  );
		});

		// Fires after a breakpoint is hit.
		$container.on( 'breakpoint', ( event, slick, breakpoint ) => {
			$container.trigger( 'c-slick:breakpoint', this, $container, event, slick, breakpoint  );
		});

		// When slider is destroyed, or unslicked.
		$container.on( 'destroy', ( event, slick ) => {
			$container.trigger( 'c-slick:destroy', this, $container, event, slick  );
		});

		// Fires when an edge is overscrolled in non-infinite mode.
		$container.on( 'edge', ( slick, direction ) => {
			$container.trigger( 'c-slick:edge', this, $container, slick, direction  );
		});

		// Fires after first initialization.
		$container.on( 'init', ( slick ) => {
			$container.trigger( 'c-slick:init', this, $container, slick );
		});

		// Fires after every re-initialization
		$container.on( 'reInit', ( slick ) => {
			$container.trigger( 'c-slick:reInit', this, $container, slick );
		});

		// Fires after position/size changes
		$container.on( 'setPosition', ( slick ) => {
			$container.trigger( 'c-slick:setPosition', this, $container, slick );
		});

		// Fires after swipe/drag
		$container.on( 'swipe', ( slick ) => {
			$container.trigger( 'c-slick:swipe', this, $container, slick );
		});

		// Fires after image loads lazily
		$container.on( 'lazyLoaded', ( event, slick, image, imageSource ) => {
			$container.trigger( 'c-slick:lazyLoaded', this, $container, event, slick, image, imageSource );
		});

		// Fires after image fails to load
		$container.on( 'lazyLoadError', ( event, slick, image, imageSource ) => {
			$container.trigger( 'c-slick:lazyLoadError', this, $container, event, slick, image, imageSource );
		});
	}


	/**
	 * Set up internal events
	 *
	 * @return  {void}
	 */
	setupInternalEvents()
	{
		const { $container } = this;

		$container.on( 'setPosition', ( ev, slick ) =>
		{
			this.reApplyVerticalHeightFix( slick );
		});
	}


	// Fixes: Vertical Slides
	// --------------------------------------------------------------------

	/**
	 * Apply a fix for vertical sliders. Without this, slides can overlap
	 * if they have different heights.
	 *
	 * Based on issue #1803 on the Slick github
	 *
	 * @link https://github.com/kenwheeler/slick/issues/1803#issuecomment-288999960
	 *
	 * @return  {void}
	 */
	applyVerticalHeightFix()
	{
		const { $container, options } = this;

		if ( !options.verticalHeightFix )
		{
			return;
		}

		const $slides = $container.find( '.slick-slide' );
		const $list   = $container.find( '.slick-list' );

		let maxHeight = -1;

		// Loop 1: Get height of tallest slide
		$slides.each( ( i, slideEl ) =>
		{
			const $slide = $( slideEl );
			const slideHeight = $slide.height();

			maxHeight = ( slideHeight > maxHeight ) ? slideHeight : maxHeight;
		});

		// Loop 2: Apply height fix to all slides
		$slides.each( ( i, slideEl ) =>
		{
			const $slide = $( slideEl );

			// Calculate the heighest slide and set a top/bottom margin for other children
			$slide.css( 'margin', Math.ceil( (maxHeight - $slide.height() ) / 2 ) + 'px 0' );

			// Add a min-height to the inner slides container
			$list.css( 'min-height', `${maxHeight}px` );

			if ( options.debugComponent )
			{
				console.info( `[c-slick] Applied verticalHeightFix (maxheight: ${maxHeight})`, { $container } );
			}
		});
	}


	/**
	 * Remove margins applied with "applyVerticalHeightFix"
	 *
	 * @param   {jQuery}  $container  jQuery slider container element
	 * @param   {object}  options     Slider options object
	 *
	 * @return  {void}
	 */
	removeVerticalHeightFix()
	{
		const { $container, options } = this;

		if ( !options.verticalHeightFix )
		{
			return;
		}

		const $slides = $container.find( '.slick-slide' );
		const $list   = $container.find( '.slick-list' );

		$slides.each( ( i, slideEl ) =>
		{
			const $slide = $( slideEl );

			$slide.css( 'margin', '0' );
			$list.css( 'min-height', '' );
		});
	}


	/**
	 * Re-apply margins applied with "applyVerticalHeightFix"
	 *
	 * Removes margins, re-calculates them, re-adds them, then tells Slick
	 * to re-calculate its slides
	 *
	 * @param   {jQuery}  $container  jQuery slider container element
	 * @param   {object}  options     Slider options object
	 *
	 * @return  {void}
	 */
	reApplyVerticalHeightFix( slick )
	{
		const { options } = this;

		if ( !options.verticalHeightFix )
		{
			return;
		}

		// Note:
		// Unfortunately, Slick's only re-calculation is "setPosition",
		// which fires AFTER slides have been re-positioned. Ideally we'd
		// want to run this code BEFORE Slick changes positions. This
		// would let us remove the margins before Slick re-calculates
		// slides. As that's not available, we're using a workaround to
		// manually trigger the slide re-calculations after we're removed
		// the margins (slick.setDimensions and slick.setHeight). This
		// isn't ideal, because Slick has just performed these functions,
		// via "Slick.prototype.setPosition", so there's some performance
		// loss here.

		this.removeVerticalHeightFix();

		// Force Slick to re-calculate slides.
		// This does the same as "Slick.prototype.setPosition", but
		// without triggering the "setPosition" event (as that would
		// create an infinite loop)
		slick.setDimensions();
		slick.setHeight();

		this.applyVerticalHeightFix();
	}

	slideOnHover()
	{
		const { options } = this;

		if ( !options.slideOnHover )
		{
			return;
		}

		let interval = -1;

		$(document).on(
		  'mouseenter',
		  '.v3-module .v3-slick-arrow',
		  (event) => {
			const trigger = $(event.target);

			if (interval) {
			  clearInterval(interval);
			}

			if (trigger.length) {
			  trigger.click();
			}

			interval = setInterval(() => {
			  if (trigger.length) {
				trigger.click();
			  }
			}, 1500);
		  }
		);

		$(document).on(
		  'mouseleave',
		  '.v3-module .v3-slick-arrow',
		  () => {
			if (interval) {
			  clearInterval(interval);
			  interval = -1;
			}
		  }
		);
	}

}


// Exports
// ============================================================================

export const CVideo = { defaultOptions, _componentInit, SlickComponent };


// Auto Init
// ============================================================================

_componentInit();
